import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ADS_PLATFORM_OPTIONS,
  HAS_ADS_OPTIONS,
} from "../../../utils/constants";
import {
  PRE_ACTIVATION_FIELD_AD_PLATFORMS,
  PRE_ACTIVATION_FIELD_BEST_CAMPAIGN_MESSAGES,
  PRE_ACTIVATION_FIELD_CURRENT_METRICS,
  PRE_ACTIVATION_FIELD_HAS_ADS,
  PRE_ACTIVATION_FIELD_MONTHLY_AD_SPEND,
  PRE_ACTIVATION_FIELD_TARGET_CPA,
  PRE_ACTIVATION_HINT_BEST_CAMPAIGN_MESSAGES,
  PRE_ACTIVATION_HINT_CURRENT_METRICS,
  PRE_ACTIVATION_HINT_MONTHLY_AD_SPEND,
  PRE_ACTIVATION_HINT_TARGET_CPA,
  PRE_ACTIVATION_PLACEHOLDER_ADS_PLATFORM_OTHER,
  PRE_ACTIVATION_PLACEHOLDER_BEST_CAMPAIGN_MESSAGES,
  PRE_ACTIVATION_PLACEHOLDER_CURRENT_METRICS,
  PRE_ACTIVATION_PLACEHOLDER_MONTHLY_AD_SPEND,
  PRE_ACTIVATION_PLACEHOLDER_TARGET_CPA,
} from "../../../i18n/keysTranslations";
import CheckGroupWithOtherField from "../components/CheckGroupWithOtherField";
import FieldBlock from "../components/FieldBlock";
import FieldError from "../components/FieldError";
import FieldHint from "../components/FieldHint";
import FieldLabel from "../components/FieldLabel";
import IntakeTextField from "../../../components/Form/IntakeTextField";
import RadioGroup from "../components/RadioGroup";
import ReadOnlySectionGrid from "../components/ReadOnlySectionGrid";

const CurrentAdsSection = ({ control, errors, readOnly }) => {
  const { t } = useTranslation();

  const hasAdsOptions = useMemo(
    () =>
      HAS_ADS_OPTIONS.map((o) => ({
        value: o.value,
        label: t(o.labelKey),
      })),
    [t]
  );

  const adPlatformOptions = useMemo(
    () =>
      ADS_PLATFORM_OPTIONS.map((o) => ({
        value: o.value,
        label: t(o.labelKey),
      })),
    [t]
  );

  return (
    <ReadOnlySectionGrid readOnly={readOnly}>
      <FieldBlock>
        <FieldLabel
          htmlFor="hasAds"
          text={t(PRE_ACTIVATION_FIELD_HAS_ADS)}
          required
        />
        <Controller
          name="hasAds"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <RadioGroup
                name="hasAds"
                options={hasAdsOptions}
                selectedValue={field.value}
                onChange={field.onChange}
                readOnly={readOnly}
              />
              <FieldError message={fieldState.error?.message} />
            </>
          )}
        />
      </FieldBlock>

      <CheckGroupWithOtherField
        name="adPlatforms"
        otherName="adPlatformsOther"
        otherPlaceholder={t(PRE_ACTIVATION_PLACEHOLDER_ADS_PLATFORM_OTHER)}
        label={t(PRE_ACTIVATION_FIELD_AD_PLATFORMS)}
        options={adPlatformOptions}
        control={control}
        errors={errors}
        readOnly={readOnly}
      />

      <FieldBlock>
        <FieldLabel
          htmlFor="monthlyAdSpend"
          text={t(PRE_ACTIVATION_FIELD_MONTHLY_AD_SPEND)}
          required
        />
        <FieldHint text={t(PRE_ACTIVATION_HINT_MONTHLY_AD_SPEND)} />
        <IntakeTextField
          name="monthlyAdSpend"
          control={control}
          rules={{}}
          errors={errors}
          fullWidth
          readOnly={readOnly}
          placeholder={t(PRE_ACTIVATION_PLACEHOLDER_MONTHLY_AD_SPEND)}
          variant="outlined"
        />
      </FieldBlock>

      <FieldBlock>
        <FieldLabel
          htmlFor="currentMetrics"
          text={t(PRE_ACTIVATION_FIELD_CURRENT_METRICS)}
        />
        <FieldHint text={t(PRE_ACTIVATION_HINT_CURRENT_METRICS)} />
        <IntakeTextField
          name="currentMetrics"
          control={control}
          rules={{}}
          errors={errors}
          fullWidth
          multiline
          minRows={4}
          readOnly={readOnly}
          placeholder={t(PRE_ACTIVATION_PLACEHOLDER_CURRENT_METRICS)}
          variant="outlined"
        />
      </FieldBlock>

      <FieldBlock>
        <FieldLabel
          htmlFor="targetCpa"
          text={t(PRE_ACTIVATION_FIELD_TARGET_CPA)}
          required
        />
        <FieldHint text={t(PRE_ACTIVATION_HINT_TARGET_CPA)} />
        <IntakeTextField
          name="targetCpa"
          control={control}
          rules={{}}
          errors={errors}
          fullWidth
          readOnly={readOnly}
          placeholder={t(PRE_ACTIVATION_PLACEHOLDER_TARGET_CPA)}
          variant="outlined"
        />
      </FieldBlock>

      <FieldBlock>
        <FieldLabel
          htmlFor="bestCampaignMessages"
          text={t(PRE_ACTIVATION_FIELD_BEST_CAMPAIGN_MESSAGES)}
        />
        <FieldHint text={t(PRE_ACTIVATION_HINT_BEST_CAMPAIGN_MESSAGES)} />
        <IntakeTextField
          name="bestCampaignMessages"
          control={control}
          rules={{}}
          errors={errors}
          fullWidth
          multiline
          minRows={4}
          readOnly={readOnly}
          placeholder={t(PRE_ACTIVATION_PLACEHOLDER_BEST_CAMPAIGN_MESSAGES)}
          variant="outlined"
        />
      </FieldBlock>
    </ReadOnlySectionGrid>
  );
};

export default CurrentAdsSection;
